import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { Check } from 'lucide-react';

const Pricing = () => {
  const { t } = useTranslation();

  const pricingData = [
    {
      service: 'Basic Website',
      description: 'WordPress website with basic features',
      price: '15,000',
      features: ['5 Pages', 'Mobile Responsive', 'Basic SEO', '3 Months Support']
    },
    {
      service: 'E-commerce Website',
      description: 'Full e-commerce solution with payment gateway',
      price: '35,000',
      features: ['Product Catalog', 'Payment Integration', 'Admin Panel', '6 Months Support']
    },
    {
      service: 'Mobile App Development',
      description: 'Native Android/iOS application',
      price: '80,000',
      features: ['Native Development', 'App Store Submission', 'API Integration', '1 Year Support']
    },
    {
      service: 'Digital Marketing Package',
      description: 'Complete SEO and Google Ads management',
      price: '8,000',
      monthly: true,
      features: ['SEO Optimization', 'Google Ads Management', 'Social Media', 'Monthly Reports']
    }
  ];

  return (
    <>
      <Helmet>
        <title>{t('pricing.title')}</title>
        <meta name="description" content="Transparent pricing for web development, mobile apps, and digital marketing services in Pattaya." />
      </Helmet>

      <section className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4" data-aos="fade-up">
              {t('pricing.title')}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-aos="fade-up" data-aos-delay="200">
              Transparent and competitive pricing for all our digital services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {pricingData.map((item, index) => (
              <div 
                key={index}
                className={`card ${index === 1 ? 'ring-2 ring-gold' : ''}`}
                data-aos="fade-up" 
                data-aos-delay={index * 100}
              >
                {index === 1 && (
                  <div className="bg-gold text-primary text-center py-2 -mt-8 -mx-8 mb-6 rounded-t-xl font-semibold">
                    Most Popular
                  </div>
                )}
                
                <h3 className="text-xl font-bold text-primary mb-2">{item.service}</h3>
                <p className="text-gray-600 mb-6 text-sm">{item.description}</p>
                
                <div className="mb-6">
                  <div className="text-3xl font-bold text-primary">
                    ฿{item.price}
                    {item.monthly && <span className="text-lg font-normal">/month</span>}
                  </div>
                  <div className="text-sm text-gray-500">
                    {t('pricing.starting_from')}
                  </div>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {item.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center space-x-3">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-600 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <button className={`w-full py-3 rounded-lg font-semibold transition-all duration-300 ${
                  index === 1 
                    ? 'bg-primary text-white hover:bg-primary-700' 
                    : 'bg-gray-100 text-primary hover:bg-primary hover:text-white'
                }`}>
                  {t('pricing.contact_us')}
                </button>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <p className="text-gray-600 mb-6">
              Need a custom solution? We offer flexible pricing for unique requirements.
            </p>
            <a 
              href="/contact" 
              className="btn-primary inline-flex items-center"
            >
              Get Custom Quote
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Pricing;
